import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { CustomValidatorsService } from '../service/custom-validators.service';
import { AuthService } from '../service/auth.service';
import { AFLocationConfirmation, AFAddressPojo } from '../model/af.model';
import { KeyValue } from '@angular/common';
import { BaseComponent } from '../base-component';
import { ValidateAndNavigateEvent, ChangeNavigationEvent, ProgressBarEvent, SkipToMainContentEvent } from '../service/events';
import { EventService } from '../service/event.service';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-i9',
  templateUrl: './i9.component.html',
  styleUrls: ['./i9.component.scss'],
  standalone: false
})
export class I9Component extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {

  coreSiteGroup: UntypedFormGroup;
  locationConfirmationData: AFLocationConfirmation;
  check: boolean = false;
  stateText: string = '';

  regexCity: RegExp = /^$|^[a-zA-Z0-9\s\-&().,'"/\\:;]+$/;
  regexCounty: RegExp = /^$|^[a-zA-Z0-9\s\-&().,'"/\\:;]+$/;
  regexAddress: RegExp = /^$|^[a-zA-Z0-9\s\-&().,'"/\\:;]+$/;
  regexState: RegExp = /^$|^[a-zA-Z0-9\s\-&().,'"/\\:;]+$/;
  regexZipCode: RegExp = /^$|^[a-zA-Z0-9\s\-&().,'"/\\:;]+$/;
  regexCountry: RegExp = /^\\d{1,5}$/;

  constructor(private fb: UntypedFormBuilder, private customValidator: CustomValidatorsService, private userService: AuthService,
    private eventBus: EventService,
    private _router: Router, private _snackBar: MatSnackBar) {
    super(eventBus, _router);
  }

  get usingCurrentAddress() { return this.coreSiteGroup.get('usingCurrentAddress') };
  get zipCode() { return this.coreSiteGroup.get('zipCode') };
  get country() { return this.coreSiteGroup.get('country') };
  get usState() { return this.coreSiteGroup.get('usState') };
  get state() { return this.coreSiteGroup.get('state') };
  get submit() { return this.coreSiteGroup.get('submit') }
  get bookmarkUrl() { return this.coreSiteGroup.get('bookmarkUrl') };
  get addressConfirmed() { return this.coreSiteGroup.get('addressConfirmed') };

  ngOnInit() {
    this.createGroup();
    this.subscribeEvents();
  }

  subscribeEvents() {
    this.userService.getLocationConfirmation().subscribe({
      next: (data: AFLocationConfirmation) => {
        this.locationConfirmationData = data;
        this.coreSiteGroup.patchValue(this.locationConfirmationData);

        if (this.locationConfirmationData.usingCurrentAddress == '0') {
          this.coreSiteGroup.patchValue(this.locationConfirmationData.changedAddress);
        }

        this.stateText = this.locationConfirmationData.usStateLookup[this.locationConfirmationData.currentAddress.usState];
        this.setValidators(this.coreSiteGroup);
      },
      error: (error: Error) => {
        console.log(error);
      }
    });

    this.sub = this.eventBus.on(
      ValidateAndNavigateEvent,
      (event): void => {
        if (event.payload.currentLink === '/locationConfirmation') {
          this.submit.setValue(false);
          this.bookmarkUrl.setValue("");
          if (event.payload.save) {
            this.submitLocationConfirmation((this.coreSiteGroup.value), event);
          } else if (event.payload.saveAndFinishLater) {
            this.bookmarkUrl.setValue(event.payload.currentLink);
            this.submitLocationConfirmation((this.coreSiteGroup.value), event);
          } else if (this.validate()) {
            this.submitLocationConfirmation((this.coreSiteGroup.value), event);
          }
        }
      }
    );

    this.eventBus.on(
      SkipToMainContentEvent,
      (event): void => {
        if (event.payload.text && event.payload.text == "/locationConfirmation") {
          let mainContentElement = document.querySelector("#main-content");//check if main-content id exists on page
          if (mainContentElement) {
            mainContentElement.scrollIntoView(true);
          }
        }
      }
    );
  }

  onFocusOutMainContent(event) {
    this.eventBus.emit(new SkipToMainContentEvent({ text: "FocusOff" }));
  }

  setValidators(control: UntypedFormGroup) {

    const usingCurrentAddress = control.get('usingCurrentAddress');
    const country = control.get('country');
    const zipCode = control.get('zipCode');

    this.setAddressValidators(usingCurrentAddress.value, country, zipCode);

    usingCurrentAddress.valueChanges
      .subscribe(usingCurrentAddress => {
        this.setAddressValidators(usingCurrentAddress, country, zipCode);
      });
  }

  setAddressValidators(value: string, country: AbstractControl, zipCode: AbstractControl) {

    if (value === "0") {
      country.setValidators([Validators.required, this.customValidator.validateRequiredField]);
      zipCode.setValidators([Validators.required, Validators.maxLength(60), Validators.pattern(this.regexZipCode), this.customValidator.validateRequiredField]);
    } else {
      country.setValidators(null);
      country.setErrors(null);
      zipCode.setValidators(null);
      zipCode.setErrors(null);
    }
    country.updateValueAndValidity();
    zipCode.updateValueAndValidity();
  }

  submitLocationConfirmation(locationConfirmation, event) {
    let addressPojo = new AFAddressPojo();
    addressPojo.streetAddress1 = locationConfirmation.streetAddress1;
    addressPojo.streetAddress2 = locationConfirmation.streetAddress2;
    addressPojo.streetAddress3 = locationConfirmation.streetAddress3;
    addressPojo.zipCode = locationConfirmation.zipCode;
    addressPojo.city = locationConfirmation.city;
    addressPojo.county = locationConfirmation.county;
    addressPojo.country = locationConfirmation.country;

    if (locationConfirmation.country == '186') {
      addressPojo.usState = locationConfirmation.usState;
    } else {
      addressPojo.state = locationConfirmation.state;
    }

    this.locationConfirmationData.changedAddress = addressPojo;
    this.locationConfirmationData.usingCurrentAddress = locationConfirmation.usingCurrentAddress;
    this.locationConfirmationData.addressConfirmed = locationConfirmation.addressConfirmed;
    this.locationConfirmationData.submit = locationConfirmation.submit;
    this.locationConfirmationData.bookmarkUrl = locationConfirmation.bookmarkUrl;

    this.userService.submitLocationConfirmation(this.locationConfirmationData).subscribe({
      next: () => {
        if (event.payload.save) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
        } else if (event.payload.saveAndFinishLater) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
          this.bookmarkUrl.setValue(event.payload.currentLink);
          this._router.navigate(['sessionTimedOut']);
        } else {
          this.eventBus.emit(new ChangeNavigationEvent({ id: event.payload.id }));
          this._router.navigateByUrl(event.payload.nextNavLink);
        }
      },
      error: (error: any) => console.log(error)
    });
  }

  valueAscOrder = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => {
    return a.value.localeCompare(b.value);
  }

  createGroup() {
    this.coreSiteGroup = this.fb.group({
      addressConfirmed: ['', Validators.requiredTrue],
      usingCurrentAddress: ['', [Validators.required]],
      streetAddress1: ['', [Validators.maxLength(600), this.customValidator.validateRequiredField, Validators.pattern(this.regexAddress)]],
      streetAddress2: ['', [Validators.maxLength(255), Validators.pattern(this.regexAddress)]],
      streetAddress3: ['', [Validators.maxLength(255), Validators.pattern(this.regexAddress)]],
      zipCode: ['', [Validators.required, Validators.maxLength(60), Validators.pattern(this.regexZipCode), this.customValidator.validateRequiredField]],
      city: ['', [Validators.maxLength(200), this.customValidator.validateRequiredField, Validators.pattern(this.regexCity)]],
      county: ['', [Validators.maxLength(100), this.customValidator.validateRequiredField, Validators.pattern(this.regexCounty)]],
      country: ['', [Validators.required, Validators.pattern(this.regexCountry)]],
      usState: [''],
      state: ['', [Validators.maxLength(100), Validators.pattern(this.regexState)]],
      submit: [false],
      bookmarkUrl: ['']
    });
  }

  validate() {
    this.submit.setValue(true);
    this.coreSiteGroup.markAllAsTouched();
    if (!this.addressConfirmed.value) {
      this.addressConfirmed.setErrors({ invalid: true });
      this.check = true;
    }
    if (this.coreSiteGroup?.invalid) {
      let isInvalid = true;
      const firstInvalidField = Object.keys(this.coreSiteGroup.controls).find(field => this.coreSiteGroup.get(field)?.invalid);
      if (this.coreSiteGroup.controls[firstInvalidField] instanceof FormArray) {
        (<FormArray>this.coreSiteGroup.controls[firstInvalidField]).controls.forEach((data: FormGroup) => {
          if (data.invalid && isInvalid) {
            this.userService.setFocus(data);
            isInvalid = false;
          }
        })
      } else {
        this.userService.setFocus(this.coreSiteGroup);
      }
    }
    return !this.coreSiteGroup.invalid;
  }

  ngAfterViewInit() {

    this.coreSiteGroup.valueChanges.subscribe(() => {
      setTimeout(() => {
        let progress = this.getPercentage(this.coreSiteGroup);
        this.eventBus.emit(new ProgressBarEvent({ id: progress }));
      }, 100);
    });

    // retain navigation on page refresh
    let navlink = this.userService.getNavLinks().find(e => e.routerLink === "/locationConfirmation");
    this.eventBus.emit(new ChangeNavigationEvent({ id: navlink.id }));
  }

  getPercentage(container: UntypedFormGroup): number {

    let errorCount = 0;
    let totalCount = 0;
    let channelArray: Array<string> = ['streetAddress1', 'streetAddress2', 'streetAddress3', 'city', 'county', 'usState', 'state'];

    for (let controlKey in container.controls) {
      if (channelArray.some(x => x === controlKey)) {
        continue;
      }
      if (container.controls.hasOwnProperty(controlKey)) {
        if (container.controls[controlKey].validator != null) {
          totalCount += container.controls[controlKey].validator.length;
        }
        if (container.controls[controlKey].errors) {
          errorCount += Object.keys(container.controls[controlKey].errors).length;
        }
      }
    }
    let percentage = ((totalCount - errorCount) / totalCount) * 100;
    return percentage;
  }

  dropDownScreenReaderState(address: any): string {
    if (address.value) {
      return 'Selected ' + this.locationConfirmationData.usStateLookup[Object.keys(this.locationConfirmationData.usStateLookup).find(e => e === address.value)];
    } else {
      return 'blank';
    }
  }

  dropDownScreenReaderCountry(address: any): string {
    if (address.value !== 'null') {
      return 'Selected ' + this.locationConfirmationData.countryLookup[Object.keys(this.locationConfirmationData.countryLookup).find(e => e === address.value)];
    } else {
      return 'blank';
    }
  }

  confirmOnBlur() {
    if (this.addressConfirmed.value) {
      this.check = false;
    } else {
      this.check = true;
    }
  }

  confirmOnFocus() {
    this.check = false;
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
